//EmployeeForm
import {
  React,
  useEffect,
  useState,
  useAuth,
  usePermission,
  useClientInfo,
  axiosInstance,
  tableStyles,
   Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  IconButton,
  Menu,
  TablePagination,
  MoreVertIcon,
  AddIcon,
  DeleteIcon,
  EditIcon,
  EmployeeFormAdd,
} from "../../utils/commonImports";

const API = "/api/employees";

function EmployeeForm() {
  // ✅ Always declare before using
  const { clientId, loading: clientLoading } = useClientInfo();

   const { token } = useAuth();

  // console.log("EMPLOYEE FORM DEBUG: ", { clientId, token });

  const canView = usePermission("view_employee");
  const canCreate = usePermission("create_employee");
  const canEdit = usePermission("edit_employee");
  const canDelete = usePermission("delete_employee");

  const [employees, setEmployees] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [filter, setFilter] = useState("All Employees");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuEmployee, setMenuEmployee] = useState(null);


  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /** Fetch employees */
  const fetchEmployees = async () => {
    try {
      const res = await axiosInstance.get(`${API}?client_id=${clientId}`);
      setEmployees(res.data);
    } catch (err) {
      console.error("❌ Fetch Error:", err);
    }
  };

  useEffect(() => {
    if (clientId && token) {
      fetchEmployees();
    }
  }, [clientId, token]);

  /** Handle actions */
  const handleEdit = (emp) => {
    setEditData(emp);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete?");
    if (!confirmDelete) return;

    try {
      await axiosInstance.delete(`${API}/${id}?client_id=${clientId}`);
      fetchEmployees();
    } catch (error) {
      console.error("❌ Delete Error:", error);
    }
  };

  const handleMenuOpen = (event, emp) => {
    setMenuAnchor(event.currentTarget);
    setMenuEmployee(emp);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuEmployee(null);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCancel = () => setShowForm(false);

  const handleSuccess = () => {
    setShowForm(false);
    fetchEmployees();
  };

  // Apply filtering
  const filteredEmployees = employees.filter((emp) => {
    if (filter === "All Employees") return true;
    if (filter === "Active") return emp.left_type === "Active";
    if (filter === "Inactive") return emp.left_type === "Inactive";
    if (filter === "Staff")
      return emp.employeedesignation?.toLowerCase() === "staff";
    if (filter === "Driver")
      return emp.employeedesignation?.toLowerCase() === "driver";
    return true;
  });

  // Paginate results
  const pagedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /** Guard: wait until AppContext finishes loading */
  if (clientLoading) {
    return <Typography sx={{ p: 2 }}>Loading employee data...</Typography>;
  }

  /** After loading done but still missing clientId or token */
  if (!clientId || !token) {
    return (
      <Typography sx={{ p: 2, color: "error.main" }}>
        No client or authentication available.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#f8fafc",
        minHeight: "100vh",
        pb: 8,
      }}
    >
      {/* Top bar with filter + add button */}
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            mt: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="employee-filter-label">Filter</InputLabel>
            <Select
              sx={{ fontSize: 12 }}
              labelId="employee-filter-label"
              id="employee-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem sx={{ fontSize: 12 }} value="All Employees">
                All Employees
              </MenuItem>
              <MenuItem sx={{ fontSize: 12 }} value="Active">
                Active
              </MenuItem>
              <MenuItem sx={{ fontSize: 12 }} value="Inactive">
                Inactive
              </MenuItem>
              <MenuItem sx={{ fontSize: 12 }} value="Staff">
                Staff
              </MenuItem>
              <MenuItem sx={{ fontSize: 12 }} value="Driver">
                Driver
              </MenuItem>
            </Select>
          </FormControl>

          {/* {canCreate && ( */}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            sx={{
              ml: 2,
              fontSize: 13,
              textTransform: "none",
              px: 1.5,
              py: 0.5,
              backgroundColor: "#3B82F6", // blue-500 sidebar border
              "&:hover": {
                backgroundColor: "#2563eb", // blue-600 for hover, as per Tailwind
              },
              boxShadow: "none",
            }}
            onClick={() => {
              setEditData(null);
              setShowForm(true);
            }}
          >
            Add
          </Button>

          {/* )} */}
        </Box>
      )}

      {/* Main table or form */}
      {showForm ? (
        <EmployeeFormAdd
          editData={editData}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
        />
      ) : (
        <>
          <Paper
            elevation={2}
            sx={{ width: "100%", overflowX: "auto", borderRadius: 2 }}
          >
           <Table size="small" sx={{ minWidth: 1000, ...tableStyles }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Employee Type</TableCell>
                  <TableCell>Salary</TableCell>
                  <TableCell>Joining</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedEmployees.map((emp) => (
                  <TableRow key={emp.employeeid} hover>
                    <TableCell>
                      {emp.employeefirstname} {emp.employeelastname}
                    </TableCell>
                    <TableCell>{emp.employeedesignationname}</TableCell>
                    <TableCell>{emp.employeemobile}</TableCell>
                    <TableCell>{emp.employeeemailaddress}</TableCell>
                    <TableCell>{emp.employeedrivertype || ""}</TableCell>
                    <TableCell>{emp.employeebasic}</TableCell>
                    <TableCell>{emp.employeedoj?.slice(0, 10)}</TableCell>
                    <TableCell>{emp.employeelefttype}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, emp)}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {pagedEmployees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary">
                        No employees to display.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredEmployees.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page"
            />
          </Paper>

          {/* Row menu */}
          <Menu
 cd server           anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            {canEdit && (
              <MenuItem
                onClick={() => {
                  handleEdit(menuEmployee);
                  handleMenuClose();
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
              </MenuItem>
            )}
            {canDelete && (
              <MenuItem
                onClick={() => {
                  handleDelete(menuEmployee.employeeid);
                  handleMenuClose();
                }}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
}

export default EmployeeForm;
